import { FC, useEffect, useContext, useState } from "react";
import { Controller } from "react-hook-form";
import Select, { GroupBase, OptionsOrGroups, SingleValue } from "react-select";

import HubInputField from "../../inputType";

import { US_STATES } from "src/constants";
import useFetch from "src/hooks/useFetch";

import { APIEndpoints } from "src/types/apiTypes";
import { ToastTypes } from "src/types";
import { ToastContext } from "src/context/toastContext";
import { PracticeFormPropsType } from "./types";
import { populatePracticeEnrollmentForm, resetPracticeFormFields } from "src/helpers/enrollment";
import { NPILookupResponse } from "NPILookup";
import { LoaderContext } from "src/context/loaderContext";
import ReactInputMask from "react-input-mask";

const EnrollmentPracticeForm: FC<PracticeFormPropsType> = ({ parentNpi, control, errors, trigger, getValues, setValue, resetField }): JSX.Element => {
  const [preservedNpi, updatePreservedNpi] = useState<string>(parentNpi);

  const { showToast } = useContext(ToastContext);
  const { setLoader, hideLoader } = useContext(LoaderContext);

  /**
   * @NPILookup calling npi lookup for details
   * @first useFetch hook for api call
   * @second useFetch error handler
   * @third useFetch success handler
   * @fourth npi lookup api trigger
   */
  const { data: npiLookupData, error: npiLookupError, mutate: lookupNpi, removeErrorAndData: clearLookupData } = useFetch<NPILookupResponse>(APIEndpoints.NPI_LOOKUP);

  /** @third */
  useEffect(() => {
    if (npiLookupData || npiLookupError) {
      hideLoader();
      if (npiLookupData) {
        if (npiLookupData.practice_data !== null) {
          showToast([`Practice Data Found`], ToastTypes.INFO);
          populatePracticeEnrollmentForm(setValue, npiLookupData, getValues("enrollment_practices_attributes.0.npi") || "");
          trigger(["enrollment_practices_attributes"]);
        }

        if (npiLookupData.practice_data === null) {
          resetPracticeFormFields(resetField);
          showToast([`No Data Found`], ToastTypes.ERROR);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [npiLookupData, npiLookupError]);

  /** @fourth */
  const callNPILookup = () => {
    const npi = getValues("enrollment_practices_attributes.0.npi");

    if (npi !== preservedNpi) {
      const npiError = errors && errors.enrollment_practices_attributes && errors.enrollment_practices_attributes[0]?.npi;

      if (
        !npi ||
        (npi && npi.length === 0) ||
        npiError
      )
        showToast([`Please enter valid NPI for lookup`], ToastTypes.ERROR);
      else {
        setLoader();
        clearLookupData();
        updatePreservedNpi(npi);
        const data = {
          'global_doctor[npi]': npi,
        };
        lookupNpi(null, null, data);
      }
    }
  };

  return (
    <>
      <Controller
        name="enrollment_practices_attributes.0.practice_name"
        control={control}
        defaultValue=""
        render={({ field, fieldState: { error } }) => (
          <HubInputField
            inputId="enrollment_practices_attributes.0.practice_name"
            inputPlaceholder="Practice Name*"
            isRequired={true}
            isRowBased={true}
            field={field}
            error={error}
          />
        )}
      />

      <div className="row">
        <div className="col-lg-6">
          <Controller
            name="enrollment_practices_attributes.0.npi"
            control={control}
            defaultValue=""
            render={({ field, fieldState: { error } }) => (
              <HubInputField
                inputId="enrollment_practices_attributes.0.npi"
                inputPlaceholder="NPI*"
                isRequired={true}
                isRowBased={true}
                isRowInsideAnother={true}
                field={field}
                error={error}
                onBlur={callNPILookup}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    callNPILookup();
                  }
                }}
              />
            )}
          />
        </div>
        <div className="col-lg-6">
          <Controller
            name="enrollment_practices_attributes.0.tax"
            control={control}
            defaultValue=""
            render={({ field, fieldState: { error } }) => (
              <HubInputField
                inputId="enrollment_practices_attributes.0.tax"
                inputPlaceholder="Tax ID"
                isRequired={false}
                isRowBased={true}
                isRowInsideAnother={true}
                field={field}
                error={error}
              />
            )}
          />
        </div>
      </div>

      <div className="row">
        <div className="col-lg-6">
          <Controller
            name="enrollment_practices_attributes.0.phone_number"
            control={control}
            defaultValue=""
            render={({ field, fieldState: { error } }) => (
              <div className="row mb-3">
                <div className="col-12">
                  <ReactInputMask
                    {...field}
                    mask="(999) 999-9999"
                    className={`form-control ${error ? "is-invalid" : ""}`}
                    onChange={(e) => field.onChange(e.target.value.replace(/\D/g, ''))}
                  >
                    {(inputProps: {}) => <input type="text" id="enrollment_practices_attributes.0.phone_number"
                      className="form-control"
                      placeholder="Phone Number* e.g. (555) 555-5555" {...inputProps} />}
                  </ReactInputMask>

                  {error && error.message ? (
                    <div dangerouslySetInnerHTML={{ __html: error.message }}
                      className="text-danger text-small mt-2"></div>
                  ) : null}
                </div>
              </div>
            )}
          />
        </div>
        <div className="col-lg-6">
          <Controller
            name="enrollment_practices_attributes.0.fax_number"
            control={control}
            defaultValue=""
            render={({ field, fieldState: { error } }) => (
              <div className="row mb-3">
                <div className="col-12">
                  <ReactInputMask
                    {...field}
                    mask="(999) 999-9999"
                    className={`form-control ${error ? "is-invalid" : ""}`}
                    onChange={(e) => field.onChange(e.target.value.replace(/\D/g, ''))}
                  >
                    {(inputProps: {}) => <input type="text"
                      id="enrollment_practices_attributes.0.fax_number"
                      className="form-control"
                      placeholder="Phone Number* e.g. (555) 555-5555" {...inputProps} />}
                  </ReactInputMask>

                  {error && error.message ? (
                    <div dangerouslySetInnerHTML={{ __html: error.message }}
                      className="text-danger text-small mt-2"></div>
                  ) : null}
                </div>
              </div>
            )}
          />
        </div>
      </div>

      <div className="row">
        <div className="col-lg-6">
          <Controller
            name="enrollment_practices_attributes.0.street"
            control={control}
            defaultValue=""
            render={({ field, fieldState: { error } }) => (
              <HubInputField
                inputId="enrollment_practices_attributes.0.street"
                inputPlaceholder="Street*"
                isRequired={true}
                isRowBased={true}
                isRowInsideAnother={true}
                field={field}
                error={error}
              />
            )}
          />
        </div>
        <div className="col-lg-6">
          <Controller
            name="enrollment_practices_attributes.0.suite"
            control={control}
            defaultValue=""
            render={({ field, fieldState: { error } }) => (
              <HubInputField
                inputId="enrollment_practices_attributes.0.suite"
                inputPlaceholder="Suite"
                isRequired={false}
                isRowBased={true}
                isRowInsideAnother={true}
                field={field}
                error={error}
              />
            )}
          />
        </div>
      </div>

      <div className="row">
        <div className="col-lg-6">
          <Controller
            name="enrollment_practices_attributes.0.city"
            control={control}
            defaultValue=""
            render={({ field, fieldState: { error } }) => (
              <HubInputField
                inputId="enrollment_practices_attributes.0.city"
                inputPlaceholder="City*"
                isRequired={true}
                isRowBased={true}
                isRowInsideAnother={true}
                field={field}
                error={error}
              />
            )}
          />
        </div>
        <div className="col-lg-6 mb-3">
          <Controller
            name="enrollment_practices_attributes.0.state"
            control={control}
            defaultValue=""
            render={({ field, fieldState: { error } }) => (
              <>
                <Select
                  options={US_STATES as unknown as OptionsOrGroups<string, GroupBase<string>>}
                  id="enrollment_practices_attributes.0.state"
                  classNames={{
                    control(_props) {
                      return `input-group ${error && error.message ? "border-danger" : ""}`
                    },
                  }}
                  placeholder="State *"
                  isSearchable={true}
                  {...field}
                  noOptionsMessage={() => "Select Option..."}
                  value={
                    US_STATES.find(
                      (option) => (option as { value: string; label: string }).value === field.value
                    ) as unknown as SingleValue<unknown>
                  }
                  onChange={(value) =>
                    field.onChange((value as { value: string; label: string }).value)
                  }
                  menuPortalTarget={document.body}
                  maxMenuHeight={250}
                  menuPlacement="auto"
                />

                {error && error.message ? (
                  <div
                    dangerouslySetInnerHTML={{ __html: error.message }}
                    className="text-danger text-small mt-2"
                  ></div>
                ) : null}
              </>
            )}
          />
        </div>
      </div>

      <div className="row">
        <div className="col-lg-6">
          <Controller
            name="enrollment_practices_attributes.0.zip"
            control={control}
            defaultValue=""
            render={({ field, fieldState: { error } }) => (
              <HubInputField
                inputId="enrollment_practices_attributes.0.zip"
                inputPlaceholder="Zip*"
                isRequired={true}
                isRowBased={true}
                isRowInsideAnother={true}
                field={field}
                error={error}
              />
            )}
          />
        </div>
      </div>
    </>
  );
};

export default EnrollmentPracticeForm;
